// ────────────────────────────────────────────────────────────────────────────
// Diagnostics-menu unlock flag.
//
// The Live Scan diagnostics surface in Settings (PaywallDiagnosticsPanel,
// BroadcastDiagnosticsPanel, IngestionDiagnosticsPanel, OcrResultsPanel) plus
// the "tap version 5 times" easter egg and the deep-link auto-open are gated
// behind `import.meta.env.DEV`. On production TestFlight builds DEV === false,
// so the panel is never in the DOM and the tap is a no-op — which means Sam
// cannot read the broadcast diagnostics on a real prod build (the exact thing
// needed to diagnose the live-scan phase-stuck bug).
//
// 2026-06-27: the DEFAULT was temporarily flipped ON so the diagnostics menu +
// scan JSON/telemetry were available on shipping builds without the hidden
// 7-tap version gesture, to support scan-accuracy work.
//
// 2026-07-01: reverted the DEFAULT back to OFF for App Store release. The
// diagnostics surface must NOT be visible to reviewers or ordinary users on a
// shipping build. All the deliberate unlock paths are kept intact (7-tap
// version gesture, VITE_DIAGNOSTICS_MENU env, #nexus-diag hash, and the
// localStorage override), so QA still gets it on demand — it just isn't on by
// default anymore.
//
// NOTE: this flag intentionally does NOT unlock the "Demo tier switcher"
// (which fakes IAP/paywall tiers) — that stays DEV-only because exposing it on
// prod would let a user fake premium entitlements.
//
// Resolution order:
//
//   1. Build-time env var `VITE_DIAGNOSTICS_MENU`. "0"/"false" forces OFF;
//      "1"/"true" forces ON.
//
//   2. `localStorage["dex.diagnostics.enabled"]`. "0"/"false" forces OFF;
//      "1"/"true" forces ON. Set via the 7-tap gesture or Safari Web Inspector.
//
//   3. URL hash `#nexus-diag`.
//      One-shot device probe; promoted to localStorage so reloads stay on.
//
//   4. Otherwise: OFF (default as of 2026-07-01, reverted for release).
// ────────────────────────────────────────────────────────────────────────────

export const DIAGNOSTICS_MENU_LS_KEY = "dex.diagnostics.enabled";
export const DIAGNOSTICS_MENU_URL_HASH = "nexus-diag";

let cached: boolean | null = null;

/**
 * Resolve the diagnostics-menu enablement.
 *
 * 2026-07-01: the DEFAULT is OFF (reverted for App Store release). The
 * diagnostics menu is unlocked only via an explicit opt-in: the 7-tap version
 * gesture / localStorage "1", the VITE_DIAGNOSTICS_MENU env var, or the
 * #nexus-diag URL hash. Ordinary users and reviewers never see it.
 */
function resolveEnabled(): boolean {
  // Env explicit override.
  try {
    const raw = (import.meta as unknown as {
      env?: Record<string, string | undefined>;
    }).env?.VITE_DIAGNOSTICS_MENU;
    if (raw === "0" || raw === "false") return false;
    if (raw === "1" || raw === "true") return true;
  } catch {
    // ignore
  }
  if (typeof window === "undefined") return false;
  // LocalStorage explicit override (set by the 7-tap gesture / Web Inspector).
  try {
    const ls = window.localStorage.getItem(DIAGNOSTICS_MENU_LS_KEY);
    if (ls === "0" || ls === "false") return false;
    if (ls === "1" || ls === "true") return true;
  } catch {
    // ignore
  }
  // URL hash on-probe; promote to LS so subsequent reloads stay on.
  const hash = window.location?.hash?.toLowerCase() ?? "";
  if (hash.includes(DIAGNOSTICS_MENU_URL_HASH)) {
    try {
      window.localStorage.setItem(DIAGNOSTICS_MENU_LS_KEY, "1");
    } catch {
      // ignore
    }
    return true;
  }
  // Default OFF (2026-07-01, reverted for release).
  return false;
}

/**
 * Returns true when the diagnostics menu should be reachable on this build.
 * Resolves at most once per page-load and caches the result. Default OFF —
 * see resolveEnabled / file header for the explicit unlock paths.
 */
export function isDiagnosticsMenuEnabled(): boolean {
  if (cached !== null) return cached;
  cached = resolveEnabled();
  return cached;
}

/** Test-only escape hatch. */
export function __resetDiagnosticsMenuCache(): void {
  cached = null;
}
